import React, { useContext, useEffect, useRef, useState } from "react";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import Thumbnail from "./Thumbnail";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import { isMobile } from "react-device-detect";
import themeContext from "../../../../context/theme/ThemeContext";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import ChildStudyThumbnailList from "./ChildStudyThumbnailList";
import StudyPreloadButton from "./StudyPreloadButton";
import { firstImageSeriesIndex } from "../../helpers/seriesModality";

/**
 * The first image series is shown as thumbnail index 0, so the viewport
 * auto-load (keyed on index 0) fires even when non-image series (SR, PDF, PR,
 * KO, ECG, SEG, RT… — see seriesModality.jsx) sort first. The series that really sits at 0 takes the
 * first renderable's index instead — a swap, not a remap — because an SR/DOC
 * tile at 0 is still rendered: two tiles with id="stack_0" made the image
 * series draw its thumbnail into the SR tile's hidden box (series with no
 * thumbnail).
 */
export const remapThumbnailIndex = (index, firstRenderable) => {
  if (firstRenderable == null || firstRenderable <= 0) return index;
  if (index === firstRenderable) return 0;
  if (index === 0) return firstRenderable;
  return index;
};

const ThumbnailList = ({
  patientName,
  patientId,
  studyData,
  priorStudyData,
  priors,
  // Share links pass false: they carry images and nothing written about them, so
  // there is no impressions panel to toggle. The tab is hidden rather than left to
  // open an empty panel.
  showImpressionsTab = true,
}) => {
  const {
    impressionHoveredStudyId,
    setImpressionHoveredStudyId,
    setSeriesHovered,
  } = useContext(ThumbnailContext);
  const thumbnailRef = useRef(null);
  const { setSeriesMenu } = useContext(studyviewerContext);
  const { theme, themeName ,getScrollBarStyleForTheme} = useContext(themeContext);
  const { setShowImpressions, showImpressions, imageLoadedOnViewport } =
    useContext(ViewportContext);
  const { getConsistentDateFormat } = useContext(studyReportContext);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        thumbnailRef.current &&
        !thumbnailRef.current.contains(event.target) &&
        isMobile
      ) {
        if (event.clientY >= 50) setSeriesMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  let count = 0;
  let timer;

  // Study Preload button entries: each series with the thumbnail index it is
  // rendered under below (priors: compacted over non-KO/PR; single study: raw,
  // with the first renderable series remapped to 0).
  // Priors parent: tiles are numbered over the non-KO/PR series in order, with
  // the first image series swapped to 0 (same rule as the single-study panel).
  const priorsParentIndices = (() => {
    const shown = (priorStudyData?.[0]?.stacks ?? [])
      .map((stack, rawIndex) => ({ stack, rawIndex }))
      .filter(({ stack }) => stack.modality !== "KO" && stack.modality !== "PR");
    const firstImage = firstImageSeriesIndex(shown.map(({ stack }) => stack));
    const byRaw = new Map();
    shown.forEach(({ rawIndex }, pos) => byRaw.set(rawIndex, remapThumbnailIndex(pos, firstImage)));
    return byRaw;
  })();
  const priorsParentPreloadEntries = () =>
    (priorStudyData?.[0]?.stacks ?? []).flatMap((stack, rawIndex) =>
      priorsParentIndices.has(rawIndex) ? [{ index: priorsParentIndices.get(rawIndex), stack }] : []
    );
  const studyPreloadEntries = () => {
    const stacks = studyData?.stacks ?? [];
    const firstRenderable = firstImageSeriesIndex(stacks);
    return stacks.flatMap((stack, index) =>
      stack.modality === "KO" || stack.modality === "PR"
        ? []
        : [{ index: remapThumbnailIndex(index, firstRenderable), stack }]
    );
  };

  const onHovered = (e, studyId) => {
    e.stopPropagation();
    timer = setTimeout(() => {
      setImpressionHoveredStudyId(studyId);
      setSeriesHovered(true);
    }, 300);
  };

  const onHoveredOut = (e) => {
    e.stopPropagation();
    clearTimeout(timer);
    setImpressionHoveredStudyId(0);
    setSeriesHovered(false);
  };
  
  return (
    <div ref={thumbnailRef} className="thumbnail-list">
      <div className="thumbnail-list-patient-details">
        <div className="thumbnail-list-patient-name">{patientName}</div>
        <div className="thumbnail-list-patient-id" style={{color: themeName === "light" ? "black" : ""}}>ID {patientId}</div>
      </div>

      {/* Thumbnail List */}
      {priors ? (
        <>
          {/* {console.log("I am inside priors thumbnail List")} */}
          <div className="thumbnail-list-studies-impression">
            <span className="thumbnail-list-studies">Studies</span>
            {showImpressionsTab && (
              <span
                className={`thumnail-list-impressions ${
                  showImpressions ? "thumnail-list-impressions-active" : ""
                }`}
                onClick={() => setShowImpressions(!showImpressions)}
              >
                Impressions
              </span>
            )}
          </div>
          <div
            className={"thumbnail-list-container "+getScrollBarStyleForTheme(themeName,"common-modal-scroll")}
          >
            <div className="h100 w100">
              {priorStudyData?.map((studyData, studyIndex) => {
                // console.log(studyData);
                if (studyIndex === 0) {
                  return (
                    <React.Fragment key={studyIndex}>
                      <div className="thumbnail-list-sticky-header">
                        <span>
                          {getConsistentDateFormat(
                            studyData.studyDateDisplay
                          ).slice(0, 9)}
                        </span>
                        <span className="thumbnail-list-header-end">
                          {getConsistentDateFormat(
                            studyData.studyDateDisplay
                          ).slice(10)}
                          <StudyPreloadButton entries={priorsParentPreloadEntries()} />
                        </span>
                      </div>

                      {studyData.stacks?.map((stack, index) => {
                        if (stack.modality === "KO" || stack.modality === "PR") return null;
                        // Priors' start index counts the parent's shown tiles.
                        count++;
                        return (
                          <div
                            id={`thumbnail_container_${stack.studyId}`}
                            className={`${
                              stack.studyId == impressionHoveredStudyId &&
                              showImpressions
                                ? "highlight-thumbnail"
                                : ""
                            }`}
                            key={index}
                            style={{ paddingTop: "20px" }}
                            onMouseEnter={(e) => onHovered(e, stack.studyId)}
                            onMouseLeave={onHoveredOut}
                          >
                            <Thumbnail
                              stack={stack}
                              index={priorsParentIndices.get(index)}
                              originalStack={studyData.originalStacks[index]}
                              heatmapStack={studyData.heatmapStacks[index]}
                              outlineStack={studyData.outlineStacks[index]}
                              isPriors={studyIndex === 0 ? false : true}
                              studyIndex={studyIndex}
                              priors={priors}
                            />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                } else {
                  if (studyIndex > 1) {
                    count += priorStudyData[studyIndex - 1].stacks.length;
                  }
                  return (
                    <ChildStudyThumbnailList
                      key={studyIndex}
                      studyData={studyData}
                      startIndex={count}
                      studyIndex={studyIndex}
                      priors={priors}
                    />
                  );
                }
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={"thumbnail-list-container "+getScrollBarStyleForTheme(themeName,"common-modal-scroll")}>
            <div className="thumbnail-list-sticky-header">
              <span>
                {getConsistentDateFormat(studyData.studyDateDisplay).slice(
                  0,
                  9
                )}
              </span>
              <span className="thumbnail-list-header-end">
                {getConsistentDateFormat(studyData.studyDateDisplay).slice(10)}
                <StudyPreloadButton entries={studyPreloadEntries()} />
              </span>
            </div>
            {(() => {
              // When PR/KO series sort first in stacks[] (seriesNumber=-10 from
              // Advance viewer), no Thumbnail mounts at index 0. The primary
              // viewport auto-load fires only when the index-0 Thumbnail completes
              // (thumbnailZeroReadyRef + autoLoadFiredRef in Thumbnail.jsx). If
              // index 0 is a PR series (filtered out), that path never runs and
              // no viewport ever loads. Fix: find the first renderable index and
              // tell Thumbnail it IS index 0 for the autoload logic.
              const firstRenderable = firstImageSeriesIndex(studyData.stacks);
              return studyData.stacks?.map((stack, index) => {
                if (stack.modality === "KO" || stack.modality === "PR") return null;
                // First image series ↔ index 0 (see remapThumbnailIndex), so the
                // viewport auto-load fires even when non-image series sort first.
                const effectiveIndex = remapThumbnailIndex(index, firstRenderable);
                if (isMobile) {
                  if (effectiveIndex === 0) {
                    return (
                      <div key={index} style={{ paddingTop: "20px" }}>
                        <Thumbnail
                          stack={stack}
                          index={effectiveIndex}
                          originalStack={studyData.originalStacks[index]}
                          heatmapStack={studyData.heatmapStacks[index]}
                          outlineStack={studyData.outlineStacks[index]}
                          isPriors={false}
                        />
                      </div>
                    );
                  }
                } else {
                  return (
                    <div key={index} style={{ paddingTop: "20px" }}>
                      <Thumbnail
                        stack={stack}
                        index={effectiveIndex}
                        originalStack={studyData.originalStacks[index]}
                        heatmapStack={studyData.heatmapStacks[index]}
                        outlineStack={studyData.outlineStacks[index]}
                        isPriors={false}
                      />
                    </div>
                  );
                }
              });
            })()}
            {imageLoadedOnViewport &&
              isMobile &&
              studyData.stacks?.map((stack, index) => {
                if (stack.modality === "KO" || stack.modality === "PR") return null;
                // Everything but the tile already shown at index 0 above, under
                // the same swapped indices (else the first image series showed
                // twice and an SR/PDF at real index 0 never showed).
                const effectiveIndex = remapThumbnailIndex(
                  index,
                  firstImageSeriesIndex(studyData.stacks)
                );
                if (effectiveIndex !== 0) {
                  return (
                    <div key={index} style={{ paddingTop: "20px" }}>
                      <Thumbnail
                        stack={stack}
                        index={effectiveIndex}
                        originalStack={studyData.originalStacks[index]}
                        heatmapStack={studyData.heatmapStacks[index]}
                        outlineStack={studyData.outlineStacks[index]}
                        isPriors={false}
                      />
                    </div>
                  );
                }
              })}
          </div>
        </>
      )}
    </div>
  );
};

export default ThumbnailList;
